import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

interface BulletPointsInputProps {
  bulletPoints: string[];
  onChange: (bulletPoints: string[]) => void;
  compact?: boolean;
  label?: string;
  maxItems?: number;
}

const BulletPointsInput: React.FC<BulletPointsInputProps> = ({
  bulletPoints = [],
  onChange,
  compact = false,
  label = 'Bullet Points',
  maxItems = 10
}) => {
  const [newItem, setNewItem] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleAddItem = () => {
    if (!newItem.trim()) {
      setError('Bullet point cannot be empty');
      return;
    }

    if (bulletPoints.length >= maxItems) {
      setError(`Maximum ${maxItems} bullet points allowed`);
      return;
    }

    if (bulletPoints.includes(newItem.trim())) {
      setError('Duplicate bullet point');
      return;
    }

    onChange([...bulletPoints, newItem.trim()]);
    setNewItem('');
    setError('');
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...bulletPoints];
    updatedItems.splice(index, 1);
    onChange(updatedItems);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <Box>
      {!compact && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', mb: 2 }}>
        <TextField
          fullWidth
          size={compact ? 'small' : 'medium'}
          placeholder={`Add ${label.toLowerCase()}`}
          value={newItem}
          onChange={(e) => {
            setNewItem(e.target.value);
            if (e.target.value.trim()) setError('');
          }}
          onKeyPress={handleKeyPress}
          error={!!error}
          helperText={error}
          sx={{ mr: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleAddItem}
          sx={{ minWidth: compact ? '36px' : 'auto', px: compact ? 1 : 2 }}
        >
          <AddIcon />
          {!compact && 'Add'}
        </Button>
      </Box>

      {bulletPoints.length > 0 && (
        <Paper variant="outlined" sx={{ maxHeight: '200px', overflow: 'auto' }}>
          <List dense={compact}>
            {bulletPoints.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleRemoveItem(index)}
                      size={compact ? 'small' : 'medium'}
                    >
                      <DeleteIcon fontSize={compact ? 'small' : 'medium'} />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{
                      variant: compact ? 'body2' : 'body1',
                    }}
                  />
                </ListItem>
                {index < bulletPoints.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default BulletPointsInput; 